/**
 * Row-level status for an external runner's `.env` mapping.
 *
 * @module features/test-suites/envMappingStatus
 * @description Pure functions over the mapping the config editor is holding,
 * the property's declared env vars, the bound persona's declared variables, and
 * the values actually filled in for the selected environment. Kept out of the
 * component so the interesting judgement — "this key is mapped but will resolve
 * to nothing" — is testable without rendering anything.
 *
 * The `$var` grammar itself is NOT reimplemented here: it comes from
 * `@chatsdet/types`, the same module the API materializes the `.env` with. A
 * second copy is how a literal `"$var.ENV.FOO"` ends up written into a
 * container.
 */
import {
  formatRunnerEnvVar,
  parseRunnerEnvSource,
  type RunnerEnvVarScope,
} from '@chatsdet/types';

/** A variable name declared somewhere the mapping can point at. */
export interface EnvMappingSourceOption {
  name: string;
  description?: string;
  /** Declared sensitive by whoever defined it — drives the disclosure warning. */
  sensitive?: boolean;
}

export interface EnvMappingContext {
  /** `envVarDefinitions` on the property — the `$var.ENV.*` namespace. */
  envDefinitions: EnvMappingSourceOption[];
  /** The bound persona's `variableDefinitions` — the `$var.USER.*` namespace. */
  userDefinitions: EnvMappingSourceOption[];
  /**
   * Keys the SELECTED environment actually has a value for. Presence is the
   * signal — the API omits names with no stored value entirely, and sensitive
   * ones come back masked rather than absent.
   */
  environmentValueKeys: ReadonlySet<string>;
  /** Null when no target environment is chosen yet (then ENV cannot resolve). */
  targetEnvironmentId: string | null;
}

/**
 * What a row will do at run time.
 *
 * `no-value` and `unknown-name` are separated on purpose: one is a variable the
 * customer declared and has not filled in for THIS environment (fixable in two
 * clicks, and the chip links there), the other is a name that does not exist at
 * all (a typo, or a variable deleted out from under the mapping). Collapsing
 * them into "broken" would send people to the wrong screen.
 */
export type EnvMappingRowStatus =
  | { kind: 'unmapped' }
  | { kind: 'literal' }
  | { kind: 'resolvable'; scope: RunnerEnvVarScope; name: string; sensitive: boolean }
  | { kind: 'no-environment'; name: string }
  | { kind: 'no-value'; name: string }
  | { kind: 'unknown-name'; scope: RunnerEnvVarScope; name: string };

/** One editable row: a `.env` key plus the source it reads from. */
export interface EnvMappingRow {
  /** Stable across edits so React keys survive a key rename. */
  id: string;
  /** The `.env` key written into the container. */
  key: string;
  /** `$var.USER.x` | `$var.ENV.x` | a literal | `''` while still unmapped. */
  source: string;
}

export function classifyEnvMappingRow(
  row: EnvMappingRow,
  ctx: EnvMappingContext
): EnvMappingRowStatus {
  if (!row.source.trim()) return { kind: 'unmapped' };

  const parsed = parseRunnerEnvSource(row.source);
  if (parsed.kind === 'literal') return { kind: 'literal' };

  const definitions = parsed.scope === 'USER' ? ctx.userDefinitions : ctx.envDefinitions;
  const definition = definitions.find((d) => d.name === parsed.name);
  if (!definition) return { kind: 'unknown-name', scope: parsed.scope, name: parsed.name };

  if (parsed.scope === 'ENV') {
    // An ENV reference resolves against ONE environment. Without a target
    // chosen there is nothing to resolve against, which is a different problem
    // from the value being blank — and a different fix.
    if (!ctx.targetEnvironmentId) return { kind: 'no-environment', name: parsed.name };
    if (!ctx.environmentValueKeys.has(parsed.name)) return { kind: 'no-value', name: parsed.name };
  }

  return {
    kind: 'resolvable',
    scope: parsed.scope,
    name: parsed.name,
    sensitive: definition.sensitive === true,
  };
}

/**
 * The same-name suggestion for an unmapped key.
 *
 * Exact match only, in ENV then USER order. No fuzzy matching: a wrong guess
 * here silently injects the wrong secret into a container, and the customer has
 * no way to tell from the outside. Suggesting nothing is the safe failure.
 */
export function suggestSourceForKey(key: string, ctx: EnvMappingContext): string | null {
  const trimmed = key.trim();
  if (!trimmed) return null;
  if (ctx.envDefinitions.some((d) => d.name === trimmed)) {
    return formatRunnerEnvVar('ENV', trimmed);
  }
  if (ctx.userDefinitions.some((d) => d.name === trimmed)) {
    return formatRunnerEnvVar('USER', trimmed);
  }
  return null;
}

/**
 * Apply the same-name suggestion to every unmapped row at once.
 *
 * This is the affordance the pilot suite lives or dies on: ~12 keys, nine of
 * them near-identical `ADMIN_URL_*` variants, and a customer who has already
 * declared all of them on the property. Mapping them one dropdown at a time is
 * the difference between "configured in a minute" and "abandoned". Rows with no
 * exact match are left alone, so what remains is exactly the set that needs a
 * human decision.
 */
export function autoMapUnmappedRows(
  rows: EnvMappingRow[],
  ctx: EnvMappingContext
): { rows: EnvMappingRow[]; mappedCount: number } {
  let mappedCount = 0;
  const next = rows.map((row) => {
    if (row.source.trim()) return row;
    const suggestion = suggestSourceForKey(row.key, ctx);
    if (!suggestion) return row;
    mappedCount += 1;
    return { ...row, source: suggestion };
  });
  return { rows: next, mappedCount };
}

/** How many unmapped rows would be filled by {@link autoMapUnmappedRows}. */
export function countAutoMappable(rows: EnvMappingRow[], ctx: EnvMappingContext): number {
  return rows.filter((r) => !r.source.trim() && suggestSourceForKey(r.key, ctx) !== null).length;
}

/**
 * Fold the editor's rows back into the wire shape.
 *
 * Unmapped rows are DROPPED, and duplicate keys collapse to the last one — both
 * because the mapping is the injection allowlist, not a scratchpad: a key with
 * no source would otherwise be persisted as an empty-string literal and written
 * into the `.env` as a variable that exists but is blank, which fails later and
 * further from the cause than not existing at all.
 */
export function rowsToMapping(rows: EnvMappingRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key || !row.source.trim()) continue;
    out[key] = row.source;
  }
  return out;
}

/** Hydrate the editor from a persisted mapping, in stable key order. */
export function mappingToRows(mapping: Record<string, string> | null): EnvMappingRow[] {
  if (!mapping) return [];
  return Object.entries(mapping)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, source]) => ({ id: `k:${key}`, key, source }));
}

/**
 * Parse a pasted `.env` / `.env.example` block into keys.
 *
 * Only the KEYS are taken; any `=value` on the line is discarded rather than
 * imported as a literal. Someone pasting their real `.env` to save typing must
 * not thereby paste their production secrets into our database — the whole
 * point of the mapping is that values live in the environment/persona stores.
 */
export function parseEnvKeyPaste(text: string): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim().replace(/^export\s+/, '');
    if (!line || line.startsWith('#')) continue;
    const key = (line.split('=')[0] ?? '').trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key) || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}
